import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

// Landing-page motion: numbers count up and sections fade in as they scroll
// into view. Browsers without IntersectionObserver (and jsdom) and users who
// prefer reduced motion get the final content with no animation.

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export const COUNT_UP_MS = 1200;

const canObserve = () => typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';

export function usePrefersReducedMotion() {
  const query = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(REDUCED_MOTION_QUERY)
      : null;
  const [reduced, setReduced] = useState(() => Boolean(query()?.matches));
  useEffect(() => {
    const media = query();
    if (!media) return undefined;
    const onChange = () => setReduced(media.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

/** True once the element has scrolled into view (and stays true). */
export function useInView(ref, { threshold = 0.1 } = {}) {
  const [inView, setInView] = useState(() => !canObserve());
  useEffect(() => {
    const node = ref.current;
    if (inView || !node || !canObserve()) return undefined;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, threshold, inView]);
  return inView;
}

/** Whether motion plays at all (supported and not reduced), and whether the element has been seen. */
function useMotion(ref) {
  const reduced = usePrefersReducedMotion();
  const inView = useInView(ref);
  const animate = !reduced && canObserve();
  return { animate, inView: inView || !animate };
}

const NUMBER_PATTERN = /\d[\d,]*(?:\.\d+)?/g;

/**
 * `text` with every number scaled by `progress` (0-1), keeping its decimals
 * and thousands separators: "540 of 1,062" at 0.5 -> "270 of 531".
 */
export function interpolateNumbers(text, progress) {
  return String(text).replace(NUMBER_PATTERN, (token) => {
    const decimals = token.includes('.') ? token.split('.')[1].length : 0;
    const target = Number(token.replace(/,/g, ''));
    const value = target * progress;
    const grouped = token.includes(',');
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: grouped,
    });
  });
}

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

/**
 * Counts the numbers in a formatted value up from zero when it scrolls into
 * view. Screen readers always get the final value.
 */
export function CountUp({ value, duration = COUNT_UP_MS }) {
  const ref = useRef(null);
  const text = value === null || value === undefined ? '' : String(value);
  const hasNumber = /\d/.test(text);
  const { animate, inView } = useMotion(ref);
  const [progress, setProgress] = useState(animate && hasNumber ? 0 : 1);

  useEffect(() => {
    if (!animate || !hasNumber) {
      setProgress(1);
      return undefined;
    }
    if (!inView) return undefined;
    let frame;
    let start;
    const step = (now) => {
      if (start === undefined) start = now;
      const t = Math.min(1, (now - start) / duration);
      setProgress(easeOutCubic(t));
      if (t < 1) frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(frame);
  }, [animate, hasNumber, inView, duration, text]);

  if (progress >= 1) return <span ref={ref}>{text}</span>;
  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <span aria-hidden="true">{interpolateNumbers(text, progress)}</span>
      <span style={VISUALLY_HIDDEN}>{text}</span>
    </span>
  );
}

/** Fades and lifts its children in when they scroll into view. */
export function Reveal({ children, delay = 0, sx, ...rest }) {
  const ref = useRef(null);
  const { animate, inView } = useMotion(ref);
  const motionSx = animate
    ? {
        opacity: inView ? 1 : 0,
        transform: inView ? 'none' : 'translateY(14px)',
        transition: `opacity 600ms ease-out ${delay}ms, transform 600ms ease-out ${delay}ms`,
      }
    : null;
  return (
    <Box ref={ref} sx={{ ...motionSx, ...sx }} {...rest}>
      {children}
    </Box>
  );
}

/** For bars that grow from zero: true once in view (always true without motion). */
export function useGrowIn(ref) {
  const { animate, inView } = useMotion(ref);
  return { animate, grown: inView };
}
